import { ReebeloStoreT } from '@lambda/reebelo/src/types';
import imgAU from '@/public/icons/country-flags/AU.svg';
import imgNZ from '@/public/icons/country-flags/NZ.svg';
import imgSG from '@/public/icons/country-flags/SG.svg';
import imgUS from '@/public/icons/country-flags/US.svg';
import imgCA from '@/public/icons/country-flags/CA.svg';
import imgUK from '@/public/icons/country-flags/UK.svg';
import imgFR from '@/public/icons/country-flags/FR.svg';
import imgDE from '@/public/icons/country-flags/DE.svg';
import imgES from '@/public/icons/country-flags/ES.svg';
import imgIT from '@/public/icons/country-flags/IT.svg';
import imgSE from '@/public/icons/country-flags/SE.svg';
import imgIE from '@/public/icons/country-flags/IE.svg';
import imgNL from '@/public/icons/country-flags/NL.svg';
import imgAT from '@/public/icons/country-flags/AT.svg';
// TODO: uncomment with the matching country-selector entries below when each store goes live.
// import imgBE from '@/public/icons/country-flags/BE.svg';
// import imgPT from '@/public/icons/country-flags/PT.svg';
// import imgFI from '@/public/icons/country-flags/FI.svg';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

// Fixed to a proper locale->language lookup covering fr/de/es alongside en: previously
// only handled 'fr' | 'en', which silently mislabeled reebelo-de visitors' language as
// "English" in the Settings/LangCountrySelector menus. Extending for reebelo-es while
// fixing the pre-existing reebelo-de gap incidentally. See RT-16780.
export const mapLocaleToLanguage = (
  locale: 'fr' | 'de' | 'es' | 'it' | 'sv' | 'nl' | 'en',
) => {
  switch (locale) {
    case 'fr':
      return t`French`;
    case 'de':
      return t`German`;
    case 'es':
      return t`Spanish`;
    case 'it':
      return t`Italian`;
    case 'sv':
      return t`Swedish`;
    case 'nl':
      return t`Dutch`;
    case 'en':
      return t`English`;
    default:
      return t`English`;
  }
};

export const getAllCountries: () => {
  flag: string;
  country: string;
  code: string;
  store: ReebeloStoreT;
  locales: string[];
}[] = () => [
  {
    flag: imgAU,
    country: t`Australia`,
    code: 'AU',
    store: 'reebelo-au',
    locales: ['en'],
  },
  {
    flag: imgCA,
    country: t`Canada`,
    code: 'CA',
    store: 'reebelo-ca',
    locales: ['en', 'fr'],
  },
  {
    flag: imgFR,
    country: t`France`,
    code: 'FR',
    store: 'reebelo-fr',
    locales: ['fr'],
  },
  {
    flag: imgDE,
    country: t`Germany`,
    code: 'DE',
    store: 'reebelo-de',
    locales: ['de'],
  },
  {
    flag: imgES,
    country: t`Spain`,
    code: 'ES',
    store: 'reebelo-es',
    locales: ['es'],
  },
  {
    flag: imgIT,
    country: t`Italy`,
    code: 'IT',
    store: 'reebelo-it',
    locales: ['it'],
  },
  {
    flag: imgNZ,
    country: t`New Zealand`,
    code: 'NZ',
    store: 'reebelo-nz',
    locales: ['en'],
  },
  {
    flag: imgSG,
    country: t`Singapore`,
    code: 'SG',
    store: 'quista',
    locales: ['en'],
  },
  {
    flag: imgSE,
    country: t`Sweden`,
    code: 'SE',
    store: 'reebelo-se',
    locales: ['sv'],
  },
  {
    flag: imgIE,
    country: t`Ireland`,
    code: 'IE',
    store: 'reebelo-ie',
    locales: ['en'],
  },
  {
    flag: imgNL,
    country: t`Netherlands`,
    code: 'NL',
    store: 'reebelo-nl',
    locales: ['nl'],
  },
  {
    flag: imgAT,
    country: t`Austria`,
    code: 'AT',
    store: 'reebelo-at',
    locales: ['de'],
  },
  // TODO(RT-17758): Enable Belgium store when it is ready (country selector goes live in a separate PR).
  // {
  //   flag: imgBE,
  //   country: t`Belgium`,
  //   code: 'BE',
  //   store: 'reebelo-be',
  //   locales: ['fr', 'nl'],
  // },
  // TODO(RT-17726): Enable Portugal store when it is ready (country selector goes live in a separate PR).
  // {
  //   flag: imgPT,
  //   country: t`Portugal`,
  //   code: 'PT',
  //   store: 'reebelo-pt',
  //   locales: ['pt'],
  // },
  // TODO(RT-17760): Enable Finland store when it is ready (country selector goes live in a separate PR).
  // {
  //   flag: imgFI,
  //   country: t`Finland`,
  //   code: 'FI',
  //   store: 'reebelo-fi',
  //   locales: ['fi'],
  // },
  {
    flag: imgUK,
    country: t`United Kingdom`,
    code: 'GB',
    store: 'reebelo-uk',
    locales: ['en'],
  },
  {
    flag: imgUS,
    country: t`United States`,
    code: 'US',
    store: 'reebelo-us',
    locales: ['en'],
  },
];

// Staging (reebelo.blue) runs as reebelo-dev, which has no country of its own
// but is set up as Singapore (see REEBELO_COUNTRY), so it borrows Singapore's
// entry - otherwise every country selector hides itself on staging.
const SELECTOR_STORE_ALIASES: Partial<Record<ReebeloStoreT, ReebeloStoreT>> = {
  'reebelo-dev': 'quista',
};

export const getCurrentCountry = (store: ReebeloStoreT) => {
  const selectorStore = SELECTOR_STORE_ALIASES[store] ?? store;

  return getAllCountries().find((country) => country.store === selectorStore);
};
